import { useEffect, useRef } from 'react'
import { isInIframe, postSetWidthByNumberMessageToReact } from './useDrag'

type DirectoryMode = '0' | '1' | '2'

// 固定宽度常量
const LEFTSIDER_WIDTH = 112 // 左侧边栏宽度
const MYMENUS_WIDTH = 124   // 我的菜单宽度
const MAINCONTENT_WIDTH = 234 // 主内容区域宽度

// 读取指定 id 元素的宽度
const getElementWidth = (id: string): number => {
  const el = document.getElementById(id) as any
  return el?.offsetWidth || el?.clientWidth || 0
}

/**
 * 根据目录模式计算有效宽度
 * @param mode 目录模式：'0'=双列, '1'=单列, '2'=纯素材
 * @param isRoot 是否在根目录
 * @param keyword 关键词
 * @param isQuick 是否快速访问
 * @param prevMode 上一个目录模式
 * @returns 有效宽度和最小宽度
 */
const calcDirectoryWidth = (
  mode: DirectoryMode,
  isRoot?: boolean,
  keyword?: string | null,
  isQuick?: boolean,
  prevMode?: string | undefined
): { effectiveWidth: number; minWidth: number } => {
  // let myMenusWidth = getElementWidth('my-menus-sider') || 0
  // // 如果是isRoot或者是keyword或者isQuick，就算myMenusWidth是0，都要用MYMENUS_WIDTH
  // if ((isRoot || keyword || isQuick) && myMenusWidth === 0) {
  //   myMenusWidth = MYMENUS_WIDTH
  // }
  // // 如果prevDirectoryMode是双列变成纯素材的话，myMenusWidth也要等于MYMENUS_WIDTH
  // if (prevMode === '0' && mode === '2' && myMenusWidth === 0) {
  //   myMenusWidth = MYMENUS_WIDTH
  // }
  // const mainContentWidth = getElementWidth('')
  let effectiveWidth: number

  if (mode === '0') {
    // 双列：1+2+3 (leftSider + myMenus + mainContent)
    effectiveWidth = LEFTSIDER_WIDTH + MYMENUS_WIDTH + MAINCONTENT_WIDTH
  } else if (mode === '1') {
    // 单列：1+3 (leftSider + mainContent)
    effectiveWidth = LEFTSIDER_WIDTH + MAINCONTENT_WIDTH
  } else {
    // 纯素材：3 (mainContent)
    effectiveWidth = MAINCONTENT_WIDTH
  }

  // 最小宽度计算：有效宽度 + 280 - mainContentWidth
  const minWidth = effectiveWidth

  return { effectiveWidth, minWidth }
}

/**
 * 目录宽度管理 Hook
 * 统一处理目录模式变化时的宽度计算和同步
 * @param directoryMode 当前目录模式
 * @param isRoot 是否在根目录
 * @param keyword 关键词
 * @param isQuick 是否快速访问
 * @param onBeforePostMessage 在postSetWidthByNumberMessageToReact之前执行的回调函数
 */
export const useDirectoryWidth = (
  directoryMode: string | undefined,
  isRoot?: boolean,
  keyword?: string | null,
  isQuick?: boolean,
  onBeforePostMessage?: () => void
) => {
  const prevDirectoryMode = useRef<string | undefined>(undefined)

  useEffect(() => {
    // 只在 iframe 环境下执行
    if (!isInIframe || !directoryMode) return

    // 初始化时，只记录当前值，不进行计算
    if (prevDirectoryMode.current === undefined) {
      prevDirectoryMode.current = directoryMode
      return
    }

    // 如果模式没有变化，不执行
    if (prevDirectoryMode.current === directoryMode) return

    const mode = directoryMode as DirectoryMode
    const { effectiveWidth, minWidth } = calcDirectoryWidth(mode, isRoot, keyword, isQuick, prevDirectoryMode.current)

    // 在postSetWidthByNumberMessageToReact之前执行回调函数
    onBeforePostMessage?.()

    // 同步宽度给外层 React
    postSetWidthByNumberMessageToReact(effectiveWidth, minWidth, 'home')

    // 更新上一次的模式值
    prevDirectoryMode.current = directoryMode
  }, [directoryMode, isRoot, keyword, isQuick, onBeforePostMessage])
}

/**
 * 手动触发宽度计算（用于菜单点击等场景）
 * @param directoryMode 目录模式
 * @param isRoot 是否在根目录
 * @param keyword 关键词
 * @param isQuick 是否快速访问
 * @param onBeforePostMessage 在postSetWidthByNumberMessageToReact之前执行的回调函数
 */
export const calculateAndSetDirectoryWidth = (
  directoryMode: string | undefined,
  isRoot?: boolean,
  keyword?: string | null,
  isQuick?: boolean,
  onBeforePostMessage?: () => void
) => {
  if (!isInIframe || !directoryMode) return

  const mode = directoryMode as DirectoryMode
  const { effectiveWidth, minWidth } = calcDirectoryWidth(mode, isRoot, keyword, isQuick, undefined)

  // 在postSetWidthByNumberMessageToReact之前执行回调函数
  onBeforePostMessage?.()

  postSetWidthByNumberMessageToReact(effectiveWidth, minWidth, 'home')
}

